"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, MapPin, Award, Users, CircleQuestionMark as HelpCircle, CircleCheck as CheckCircle2, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqItems = [{
  id: "service-area",
  icon: MapPin,
  question: "What specific Decatur neighborhoods and service areas do you cover?",
  answer: "Our primary focus is within the City of Decatur and established neighboring DeKalb County communities. We regularly complete projects in Oakhurst, Winnona Park, Great Lakes, Chelsea Heights, Downtown Decatur, and Lenox Place. For whole-home renovations and major additions, we also serve select contiguous neighborhoods such as Kirkwood, East Lake, and Candler Park.",
  highlight: "Primary focus: City of Decatur & contiguous DeKalb neighborhoods"
}, {
  id: "licensing-insurance",
  icon: ShieldCheck,
  question: "How do you handle safety, code compliance, and home protection on-site?",
  answer: "We prioritize clear safety standards, code-compliant building practices, and structured jobsite management on every project. Our team implements dedicated dust containment, surface protection, and daily cleanup protocols to safeguard your living spaces throughout construction while adhering to local building codes.",
  highlight: "Jobsite protection • Code-compliant methods • Safety protocols"
}, {
  id: "track-record",
  icon: Award,
  question: "What background and experience does your team bring to Decatur homes?",
  answer: "Our carpenters and project leaders bring extensive residential remodeling experience focused on the architectural heritage and structural nuances of Decatur homes. We handle custom kitchen renovations, bathroom updates, primary suite additions, finished basements, and covered outdoor living spaces designed for lasting quality.",
  highlight: "Residential remodeling expertise • Decatur architectural focus"
}, {
  id: "project-management",
  icon: Users,
  question: "How is my project managed on-site on a day-to-day basis?",
  answer: "Every renovation is organized through structured on-site supervision to coordinate daily framing, trade work, and finish details. Homeowners receive ongoing progress updates, schedule visibility, and clear communication throughout the build alongside our experienced trade specialists.",
  highlight: "On-site supervision • Regular schedule updates • Coordinated trade work"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState<string>("service-area");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="about-faq" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24">
      {/* Decorative architectural background accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 -left-48 h-96 w-96 rounded-full bg-secondary/60 blur-3xl" />
        <div className="absolute bottom-10 right-0 h-80 w-80 rounded-full bg-accent/40 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Centered Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={containerVariants} className="mx-auto max-w-3xl text-center">
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2">
            <Badge variant="outline" className="border-border bg-secondary/80 px-3 py-1 font-sans text-xs font-semibold tracking-wider text-foreground uppercase">
              <HelpCircle className="size-3.5 text-chart-1" />
              Clear Answers
            </Badge>
          </motion.div>

          <motion.h2 variants={itemVariants} className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Frequently asked questions
          </motion.h2>

          <motion.p variants={itemVariants} className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Straightforward details on our service area, safety practices, renovation workflow, and project communication.
          </motion.p>
          
          <motion.div variants={itemVariants} className="mt-6 flex justify-center">
            <div className="h-0.5 w-16 bg-chart-1/80" />
          </motion.div>
        </motion.div>

        {/* Accordion Container */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mx-auto mt-12 max-w-3xl">
          <Accordion type="single" collapsible value={activeItem} onValueChange={val => setActiveItem(val)} className="space-y-4">
            {faqItems.map((item, index) => {
            const IconComponent = item.icon;
            return <AccordionItem key={item.id} value={item.id} data-index={index} className="rounded-lg border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-chart-1/40 hover:shadow-sm">
                  <AccordionTrigger className="px-5 py-5 text-left transition-colors hover:no-underline sm:px-6">
                    <div className="flex items-start gap-4 pr-3">
                      <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-secondary text-chart-1">
                        <IconComponent className="size-4.5" />
                      </div>
                      <span className="font-serif text-base font-semibold tracking-normal text-card-foreground sm:text-lg">
                        {item.question}
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-5 pt-0 pb-6 text-sm leading-relaxed text-muted-foreground sm:px-6 sm:text-base">
                    <div className="pl-0 sm:pl-13">
                      <p className="text-muted-foreground">{item.answer}</p>
                      
                      <div className="mt-4 flex items-center gap-2 rounded-md border border-border/80 bg-secondary/50 px-3.5 py-2.5 text-xs font-medium text-foreground sm:text-sm">
                        <CheckCircle2 className="size-4 shrink-0 text-chart-1" />
                        <span>{item.highlight}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </motion.div>

        {/* Bottom Consultation Callout Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={itemVariants} className="mx-auto mt-12 max-w-3xl rounded-xl border border-border bg-card p-6 shadow-xs sm:p-8">
          <div className="flex flex-col items-center justify-between gap-6 text-center sm:flex-row sm:text-left">
            <div className="space-y-1">
              <h3 className="font-serif text-lg font-semibold text-foreground sm:text-xl">
                Have a specific question about your home?
              </h3>
              <p className="text-sm text-muted-foreground">
                Speak directly with our Decatur design and construction team about your project ideas.
              </p>
            </div>

            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Button asChild className="bg-chart-1 text-primary-foreground shadow-xs hover:bg-chart-1/90">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Book a consultation</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}